/** Hotel setup mutations: upsert / delete a curated operational fact. NON-OPTIMISTIC. */

import { type UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { unwrap, unwrapRaw } from '../queries/_helpers';
import { queryKeys } from '../query-keys';
import { useApiClient } from '../react/ApiClientProvider';
import { type HotelFact, hotelFactSchema } from '../responses';

/** Variables for an upsert: the fact `category` (path) and its `value` (body). */
export interface UpsertHotelFactVariables {
  category: string;
  value: string;
}

/**
 * `PUT /setup/facts/:category` — create or replace the fact for a category.
 * Saving publishes the value into the AI knowledge layer and counts toward the
 * onboarding activation gate; the whole facts query is invalidated on success so
 * the required/allowed badges and values re-read from the server.
 */
export function useUpsertHotelFact(
  tenantId: string,
  hotelId: string,
): UseMutationResult<HotelFact, unknown, UpsertHotelFactVariables> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ category, value }: UpsertHotelFactVariables) => {
      const result = await client.PUT(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/setup/facts/{category}',
        {
          params: { path: { tenantId, hotelId, category } },
          body: { value },
          parseAs: 'json',
        },
      );
      return unwrap(result, hotelFactSchema);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.hotelFacts(tenantId, hotelId) });
    },
  });
}

/** Variables for a delete: the fact `category` to clear. */
export interface DeleteHotelFactVariables {
  category: string;
}

/**
 * `DELETE /setup/facts/:category` — clear the fact for a category (204, no body).
 * Deactivates the linked knowledge document backend-side; invalidates the facts
 * query so the row falls back to its empty/"Missing" state.
 */
export function useDeleteHotelFact(
  tenantId: string,
  hotelId: string,
): UseMutationResult<void, unknown, DeleteHotelFactVariables> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ category }: DeleteHotelFactVariables) => {
      const result = await client.DELETE(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/setup/facts/{category}',
        {
          params: { path: { tenantId, hotelId, category } },
          parseAs: 'json',
        },
      );
      unwrapRaw(result);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.hotelFacts(tenantId, hotelId) });
    },
  });
}
